import React, { useEffect, useState, useContext, useRef } from "react";
import { Table, Spinner, Alert, Container, Card, Button, Modal, Form, ProgressBar } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "views/pages/components/HusariaHeader";

const Recordings = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const visibleColumns = [
        { key: "recording_name", label: "Recording Name" },
        { key: "recording_filename", label: "File Name" },
        { key: "tools", label: "Tools" },
        { key: "recording_file_size", label: "File Size" },
        { key: "insert_date", label: "Date" },
        { key: "recording_description", label: "Description" },
    ];
    const tableName = "v_recordings";
    const title = "Recordings";
    const parentName = "HUCS";
    const rowKey = "recording_uuid";
    
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [uploadModal, setUploadModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);
    const [rowToEdit, setRowToEdit] = useState(null);
    const [editName, setEditName] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [playingRow, setPlayingRow] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [playProgress, setPlayProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(null);
    const [uploadData, setUploadData] = useState({
        file: null,
        name: "",
        description: ""
    });
    const navigate = useNavigate();
    const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
    const site_uuid = currentSite?.site_uuid || null;
    const fetchRows = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${serverHost}/get_recordings/${encodeURIComponent(site_uuid)}`
            );
            if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);
            const data = await response.json();
            console.log(data)
            setRows(data.results || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
    }, [tableName]);

    const toggleDeleteModal = (row = null) => {
        setRowToDelete(row);
        setDeleteModal(!deleteModal);
    };

    const toggleUploadModal = () => {
        setUploadModal(!uploadModal);
        if (!uploadModal) {
            // Reset form when opening
            setUploadData({
                file: null,
                name: "",
                description: ""
            });
        }
    };

    // Open the edit modal for a given row, pre-filling the current name
    const openEditModal = (row) => {
        setRowToEdit(row);
        setEditName(row?.recording_name || "");
        setEditModal(true);
    };

    const closeEditModal = () => {
        setEditModal(false);
        setRowToEdit(null);
        setEditName("");
    };

    const handleSaveName = async () => {
        if (!rowToEdit) return;
        if (!editName.trim()) {
            alert("Please enter a recording name");
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${serverHost}/rename${rowToEdit["path"]}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ recording_name: editName.trim(), recording_uuid: rowToEdit[rowKey] }),
            });
            if (!response.ok) throw new Error("Failed to update recording name");
            await response.json();

            closeEditModal();
            fetchRows();
        } catch (err) {
            alert(`Error updating recording: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!rowToDelete) return;
        try {
            const response = await fetch(`${serverHost}/delete_recording/${rowToDelete[rowKey]}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) throw new Error("Delete failed");
            await response.json();
            toggleDeleteModal();
            fetchRows();
        } catch (err) {
            alert(`Error deleting recording: ${err.message}`);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav'];
            if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav)$/i)) {
                alert("Please upload a valid audio file (MP3 or WAV)");
                return;
            }
            setUploadData(prev => ({ 
                ...prev, 
                file,
                // Auto-populate name from filename if empty
                name: prev.name || file.name.replace(/\.(mp3|wav)$/i, '')
            }));
        }
    };

    const handleUpload = async () => {
        if (!uploadData.file) {
            alert("Please select a file to upload");
            return;
        }
        if (!uploadData.name.trim()) {
            alert("Please enter a recording name");
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", uploadData.file);
            formData.append("name", uploadData.name);
            formData.append("description", uploadData.description);

            const response = await fetch(`${serverHost}/upload_recording/${site_uuid}`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) throw new Error("Upload failed");
            await response.json();
            
            toggleUploadModal();
            fetchRows();
            alert("Recording uploaded successfully!");
        } catch (err) {
            alert(`Error uploading recording: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (row) => {
        try {
            const response = await fetch(`${serverHost}/download/${row["path"]}`);
            if (!response.ok) throw new Error("Download failed");
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = row.recording_filename || 'recording.wav';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            alert(`Error downloading recording: ${err.message}`);
        }
    };

    // Stop and tear down any active playback
    const stopPlayback = () => {
        if (audioRef.current) {
            const audio = audioRef.current;
            audio.pause();
            audio.onerror = null;      // detach before touching src
            audio.removeAttribute("src");
            audio.load();               // reset internal state cleanly
            audioRef.current = null;
        }
        setPlayingRow(null);
        setIsPaused(false);
        setPlayProgress(0);
        setCurrentTime(0);
        setDuration(0);
    };

    const handlePlay = (row) => {
        const idValue = row[rowKey];

        // If this row is already loaded, treat the button as play/pause
        if (playingRow && playingRow[rowKey] === idValue && audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play();
                setIsPaused(false);
            } else {
                audioRef.current.pause();
                setIsPaused(true);
            }
            return;
        }

        // Otherwise stop whatever was playing and start this recording
        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audio = new Audio(`${serverHost}/download/${row["path"]}`);
        audioRef.current = audio;
        setIsPaused(false);
        setPlayProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setPlayingRow(row);

        audio.addEventListener("loadedmetadata", () => {
            setDuration(audio.duration || 0);
        });
        audio.addEventListener("timeupdate", () => {
            setCurrentTime(audio.currentTime || 0);
            if (audio.duration) {
                setPlayProgress((audio.currentTime / audio.duration) * 100);
            }
        });
        audio.addEventListener("ended", () => {
            stopPlayback();
        });
        audio.addEventListener("error", () => {
            const mediaError = audio.error;
            console.error("Audio playback error:", mediaError);

            const errorMessages = {
                1: "Playback aborted",
                2: "Network error",
                3: "Audio decoding failed",
                4: "Audio format not supported",
            };
            const message = mediaError
                ? errorMessages[mediaError.code] || `Unknown error (code ${mediaError.code})`
                : "Unknown playback error";

            alert(`Error playing recording: ${message}`);
            stopPlayback();
        });

        audio.play().catch((err) => {
            alert(`Error playing recording: ${err.message}`);
            stopPlayback();
        });
    };

    // Make sure playback stops if the component unmounts
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
            }
        };
    }, []);

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "-";
        const kb = bytes / 1024;
        const mb = kb / 1024;
        if (mb >= 1) {
            // return `${mb.toFixed(2)} MB`;
        }
        return "-"
        // return `${kb.toFixed(2)} KB`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const singularTitle = title.endsWith("s") ? title.slice(0, -1) : title;

    if (loading)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                    Loading {title?.toLowerCase() || "data"}...
                </p>
            </div>
        );

    if (error)
        return (
            <Alert variant="danger" className="mt-5 text-center">
                {error}
            </Alert>
        );

    return (
        <>
            <HusariaHeader name={title} parentName={parentName} padding="4.5rem"/>

            {playingRow && (
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1030,
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        borderBottom: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                        padding: "0.5rem 1rem",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant={isDark ? "light" : "outline-secondary"}
                            size="sm"
                            onClick={() => handlePlay(playingRow)}
                        >
                            {isPaused ? "▶" : "❚❚"}
                        </Button>
                        <div style={{ flex: 1 }}>
                            <div
                                className="d-flex justify-content-between mb-1"
                                style={{
                                    fontSize: "0.85rem",
                                    color: isDark ? "#e2e8f0" : "#212529",
                                }}
                            >
                                <span className="fw-bold">
                                    {playingRow.recording_name || "Playing recording"}
                                </span>
                                <span>
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </span>
                            </div>
                            <ProgressBar
                                now={playProgress}
                                style={{ height: "6px" }}
                            />
                        </div>
                        <Button
                            variant={isDark ? "outline-light" : "outline-secondary"}
                            size="sm"
                            onClick={stopPlayback}
                        >
                            ✕
                        </Button>
                    </div>
                </div>
            )}

            <Container fluid className="mt--6">
                <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
                    <Card.Header
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                            color: isDark ? "#e2e8f0" : "#111",
                        }}
                        className="d-flex justify-content-between align-items-center"
                    >
                        <div className="d-flex gap-2">
                            <Button variant="info" size="sm" onClick={fetchRows}>
                                Refresh
                            </Button>
                            <Button variant="primary" size="sm" onClick={toggleUploadModal}>
                                + Upload {singularTitle}
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                        <div style={{ overflowX: "auto" }}>
                            <Table
                                striped
                                hover
                                responsive
                                bordered
                                className={`mb-0`}
                                style={{ minWidth: "1000px", backgroundColor: isDark ? "#1f2937" : "#fff", color: isDark ? "#e2e8f0" : "#212529" }}
                            >
                                <thead
                                    style={{
                                        backgroundColor: isDark ? "#1f2937" : "#fff",
                                        fontWeight: "bold",
                                        color: isDark ? "#e2e8f0" : "#212529",
                                    }}
                                >
                                    <tr>
                                        {visibleColumns.map((col) => (
                                            <th key={col.key || col.label} className="text-center">
                                                {col.label}
                                            </th>
                                        ))}
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const idValue = row[rowKey];
                                        return (
                                            <tr
                                                key={idValue}
                                                onClick={() => openEditModal(row)}
                                                style={{ color: isDark ? "#e2e8f0" : "#212529", cursor: "pointer" }}
                                            >
                                                {visibleColumns.map((col, index) => {
                                                    let value = row[col.key] ?? "-";
                                                    
                                                    // Format special columns
                                                    if (col.key === "recording_file_size") {
                                                        value = formatFileSize(value);
                                                    } else if (col.key === "recording_date") {
                                                        value = formatDate(value);
                                                    } else if (col.key === "tools") {
                                                        value = row.tools || "-";
                                                    }
                                                    
                                                    const isLink = index === 0;

                                                    return (
                                                        <td key={`${idValue}-${col.key}`} className="text-center">
                                                            {isLink ? (
                                                                <span
                                                                    style={{
                                                                        color: isDark ? "#60a5fa" : "#0d6efd",
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {value}
                                                                </span>
                                                            ) : (
                                                                <span style={{ fontWeight: 400 }}>{value}</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td className="text-center">
                                                    <div
                                                        className="d-flex gap-2 justify-content-center"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handlePlay(row)}
                                                        >
                                                            {playingRow && playingRow[rowKey] === idValue && !isPaused
                                                                ? "❚❚ Pause"
                                                                : "▶ Play"}
                                                        </Button>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={() => handleDownload(row)}
                                                        >
                                                            Download
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => toggleDeleteModal(row)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            </Container>

            {/* Upload Modal */}
            <Modal show={uploadModal} onHide={toggleUploadModal} size="lg">
                <Modal.Header
                            closeButton
                            style={{
                                backgroundColor: isDark ? "#1f2937" : "#fff",
                            }}
                        >
                     <Modal.Title style={{color: isDark ? "#e2e8f0" : "#212529",}}>Upload Recording</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        backgroundColor: isDark ? "#111827" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Recording Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter recording name"
                                value={uploadData.name}
                                onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
                                style={{
                                    backgroundColor: isDark ? "#374151" : "#fff",
                                    color: isDark ? "#e2e8f0" : "#212529",
                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Audio File (MP3 or WAV) *</Form.Label>
                            <Form.Control
                                type="file"
                                accept="audio/mpeg,audio/wav,audio/mp3,.mp3,.wav"
                                onChange={handleFileChange}
                                style={{
                                    backgroundColor: isDark ? "#374151" : "#fff",
                                    color: isDark ? "#e2e8f0" : "#212529",
                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                }}
                            />
                            {uploadData.file && (
                                <Form.Text style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Selected: {uploadData.file.name} ({(uploadData.file.size / 1024 / 1024).toFixed(2)} MB)
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={uploadData.description}
                                onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Enter a description for this recording..."
                                style={{
                                    backgroundColor: isDark ? "#374151" : "#fff",
                                    color: isDark ? "#e2e8f0" : "#212529",
                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                    }}
                >
                    <Button variant="light" onClick={toggleUploadModal} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleUpload} 
                        disabled={uploading || !uploadData.file || !uploadData.name.trim()}
                    >
                        {uploading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Uploading...
                            </>
                        ) : (
                            "Upload"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Recording Name Modal */}
            <Modal show={editModal} onHide={closeEditModal}>
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    <Modal.Title style={{color: isDark ? "#e2e8f0" : "#212529"}}>Edit {singularTitle} Name</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        backgroundColor: isDark ? "#111827" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Recording Name *</Form.Label>
                            <Form.Control
                                type="text"
                                autoFocus
                                placeholder="Enter recording name"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSaveName();
                                    }
                                }}
                                style={{
                                    backgroundColor: isDark ? "#374151" : "#fff",
                                    color: isDark ? "#e2e8f0" : "#212529",
                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                    }}
                >
                    <Button variant="light" onClick={closeEditModal} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveName}
                        disabled={saving || !editName.trim()}
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={deleteModal} onHide={toggleDeleteModal}>
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    <Modal.Title style={{color: isDark ? "#e2e8f0" : "#212529"}}>Delete {singularTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        backgroundColor: isDark ? "#111827" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    Are you sure you want to delete the recording <strong>{rowToDelete?.recording_name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                    }}
                >
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                    <Button variant="secondary" onClick={toggleDeleteModal}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Recordings;